import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { EditablePageShell } from "@/components/site/EditablePageShell";
import { pageQueryOptions } from "@/lib/pages.functions";
import { supabase } from "@/integrations/supabase/client";
import { SHARE_IMAGES } from "@/lib/share-images";

export const Route = createFileRoute("/blog")({
  loader: ({ context }) => context.queryClient.ensureQueryData(pageQueryOptions("blog")),
  head: ({ loaderData }) => ({
    meta: [
      { title: loaderData?.title || "Blog — Glow Long Golf" },
      { name: "description", content: loaderData?.meta_description || "Night-golf tips, glow-ball gift guides, event planning ideas, and behind-the-scenes from Glow Long Golf." },
      { property: "og:title", content: loaderData?.title || "The Glow Long Golf Blog" },
      { property: "og:description", content: loaderData?.meta_description || "Night-golf tips, gift guides, and event ideas." },
      { property: "og:url", content: "https://glowlonggolf.com/blog" },
      { property: "og:image", content: SHARE_IMAGES.cometSwing },
      { name: "twitter:image", content: SHARE_IMAGES.cometSwing },
    ],
    links: [{ rel: "canonical", href: "https://glowlonggolf.com/blog" }],

    scripts: [{
      type: "application/ld+json",
      children: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "Blog",
        name: "The Glow Long Golf Blog",
        url: "/blog",
        publisher: { "@type": "Organization", name: "Glow Long Golf" },
      }),
    }],
  }),
  errorComponent: ({ error }) => <div className="p-8 text-sm text-destructive" role="alert">{error.message}</div>,
  notFoundComponent: () => <div className="p-8 text-sm text-muted-foreground">Not found.</div>,
  component: () => {
    const { data: posts } = useQuery({
      queryKey: ["blog-list"],
      queryFn: async () => {
        const { data } = await supabase.from("blog_posts").select("slug,title,excerpt,published_at").eq("is_published", true).order("published_at", { ascending: false });
        return data ?? [];
      },
    });
    return (
      <EditablePageShell slug="blog" defaults={{ eyebrow: "Journal", title: "The Glow Long blog", lead: "Night-golf tips, gift guides, and behind-the-scenes." }}>
        <section className="mx-auto max-w-5xl px-4 pb-16 sm:px-6 lg:px-8">
          <ul className="grid gap-6 md:grid-cols-2">
            {(posts ?? []).map((p: any) => (
              <li key={p.slug}>
                <Link to="/blog/$slug" params={{ slug: p.slug }} className="block rounded-2xl border border-border bg-card p-6 transition hover:shadow-glow">
                  <h2 className="font-display text-xl font-semibold">{p.title}</h2>
                  <p className="mt-2 text-sm text-muted-foreground">{p.excerpt}</p>
                  <span className="mt-3 inline-block text-xs text-muted-foreground">{new Date(p.published_at).toLocaleDateString()}</span>
                </Link>
              </li>
            ))}
          </ul>
        </section>
      </EditablePageShell>
    );
  },
});
